/* pl-promo.js
 * ============
 * JS twin of snippets/promo-resolver.liquid. One function decides what price
 * and badge a shopper sees for a variant; theme.js (_updatePromoBadge, plLcInfo)
 * and main-collection.js should call this instead of carrying their own rules.
 *
 * The two implementations MUST stay rule-identical. If you change one, change
 * the other in the same deploy. The shared test table lives in the audit doc.
 *
 * resolve(productData, variant, opts) -> {
 *   displayPrice: int cents,
 *   comparePrice: int cents (0 = nothing struck),
 *   badgeType: 'pro' | 'campaign' | 'sale' | 'none',
 *   badgeLabel: string,
 *   badgePct: int,
 *   lcActive: bool  // variant-level promo drove the price
 * }
 *
 * productData: { type, tags: [], tag_discount?: number }  // tag_discount may be
 *   a fraction (0.25, from mc-catalog-data) or a percent (25); both accepted.
 * variant: { price, compare_at_price, last_chance?: bool, promo_tier?: number }
 *   last_chance comes from Liquid (metafields are absent from variant JSON) -
 *   pass it from data-lc-variants membership where that's the only source.
 * opts (optional): defaults from window.themeVariables.settings.
 */
(function () {
  'use strict';

  // "N% Off" tags: the percent is parsed from the tag itself, so any percent works.
  var TAG_RE = /^(\d{1,2})% off$/;

  // Campaign slots, emitted by snippets/js-variables.liquid as settings.campaigns:
  //   [{ percent, label, style, code, percentBg, nameBg, nameText }, ...]
  // Keyed by percent for lookup. Memoized because plBuildCatalog resolves every
  // variant on a large collection.
  // MIRRORS snippets/promo-resolver.liquid and snippets/campaign-lookup.liquid.
  var _slotCacheSrc = null, _slotCacheVal = null;
  function slotMap(s) {
    var src = s.campaigns || [];
    if (src === _slotCacheSrc) return _slotCacheVal;
    var map = {};
    src.forEach(function (c) {
      var k = parseInt(c && c.percent, 10);
      if (k > 0 && k < 100) map[k] = c;
    });
    _slotCacheSrc = src;
    _slotCacheVal = map;
    return map;
  }

  function settings(opts) {
    var s = (window.themeVariables && window.themeVariables.settings) || {};
    return opts || s;
  }

  function tagPercent(productData) {
    // Prefer an explicit tag_discount if the caller already computed one.
    var td = productData && productData.tag_discount;
    if (td != null && td !== 0) {
      var n = Number(td);
      if (!isNaN(n) && n > 0) return n < 1 ? Math.round(n * 100) : Math.round(n);
    }
    var tags = (productData && productData.tags) || [];
    for (var i = 0; i < tags.length; i++) {
      var m = TAG_RE.exec(String(tags[i]).trim().toLowerCase());
      if (m) return parseInt(m[1], 10);
    }
    return 0;
  }

  // PL 2026-10-08 audit: match snippets/promo-resolver.liquid rounding exactly.
  // Liquid computes `price | times: pct | divided_by: 100` (integer division, so
  // the DISCOUNT is floored) and then subtracts; Math.round on the final price
  // was 1 cent off on odd amounts. A fractional pct (e.g. a 6.25 Pro bonus) is a
  // float in Liquid, so there the subtraction is followed by `| round`.
  function applyPct(cents, pct) {
    if (pct % 1 === 0) return cents - Math.floor(cents * pct / 100);
    return Math.round(cents - cents * pct / 100);
  }

  function resolve(productData, variant, opts) {
    var s = settings(opts);
    var price = Number(variant && variant.price) || 0;
    var compareRaw = Number(variant && variant.compare_at_price) || 0;
    var compare = compareRaw > price ? compareRaw : 0;

    var out = {
      displayPrice: price, comparePrice: compare,
      badgeType: 'none', badgeLabel: '', badgePct: 0, lcActive: false,
      style: 'pill_percent', code: '', percentBg: '', nameBg: '', nameText: ''
    };
    if (!price) return out;

    // Exclusions (mirrors promo_exclude_all_sale_badges_tags); callers that resolved
    // the exclusion server-side can pass productData.excluded directly.
    var excluded = !!(productData && productData.excluded);
    var exclList = String(s.promoExcludeAllSaleBadgesTags || 'Bundle Deal, Clearance').split(',');
    var tags = (productData && productData.tags) || [];
    for (var i = 0; i < exclList.length; i++) {
      var e = exclList[i].trim();
      if (e && tags.indexOf(e) !== -1) { excluded = true; break; }
    }

    // S2 tag campaign (respects the enable_promotion_discounts master switch)
    var tagPct = (s.enablePromotionDiscounts === false) ? 0 : tagPercent(productData);

    // S3 last-chance variant campaign. Two modes:
    // CALCULATED (no compare-at): native discount exists only at checkout, so the
    //   theme computes price x (1 - pct). Competes with Pro.
    // BAKED (compare_at > price): markdown already inside price, compare_at is the
    //   strike; the metafield just names the campaign. Does NOT compete with Pro.
    //   Any "X% Off" tag is ignored for a baked variant - the visible markdown IS
    //   the campaign; a stacked checkout tag discount is an admin data problem.
    var lcPct = 0, lcBaked = false;
    if (s.enableLastChancePromo && variant && variant.last_chance) {
      if (compare > 0) {
        lcBaked = true;
      } else {
        // Per-variant depth first; global percent is the flat-campaign fallback.
        lcPct = parseInt(variant.promo_tier || s.lastChancePromoPercent || 25, 10) || 0;
        if (lcPct <= 0 || lcPct >= 100) lcPct = 0;
      }
    }

    // Slot metadata for the matched tag percent
    var slot = tagPct > 0 ? (slotMap(s)[tagPct] || null) : null;
    if (slot) {
      out.style = slot.style || 'pill_percent';
      out.code = slot.code || '';
      out.percentBg = slot.percentBg || '';
      out.nameBg = slot.nameBg || '';
      out.nameText = slot.nameText || '';
    }

    var campaignPct = Math.max(tagPct, lcPct);
    var lcDrove = lcPct > tagPct && lcPct === campaignPct;
    if (lcBaked) { campaignPct = 0; tagPct = 0; }
    if (excluded) campaignPct = 0;

    // A code-gated campaign is not applied until the shopper enters the code, so
    // the price stays at full and it cannot compete with the Pro discount.
    var codePct = 0;
    if (out.code && !lcBaked && tagPct > 0 && tagPct === campaignPct) {
      codePct = campaignPct;
      campaignPct = 0;
    }

    // S4 Pro Stylist (Bundle Deal type excluded). Callers that only know
    // eligibility server-side can pass productData.pro_eligible instead of type.
    var proPct = 0;
    if (s.isProStylist) {
      var eligible = productData && (
        productData.pro_eligible != null ? !!productData.pro_eligible
                                         : productData.type !== 'Bundle Deal');
      if (eligible) proPct = parseInt(s.stylistDiscount || '0', 10) || 0;
    }

    // Ties go to the CAMPAIGN, matching snippets/promo-resolver.liquid. The
    // Fall Sale has a 20% tier equal to the standing Pro rate, and >= badged
    // those variants as ordinary Pro pricing inside a sale collection.
    if (proPct > 0 && proPct > campaignPct) {
      out.displayPrice = applyPct(price, proPct); // PL 2026-10-08 audit: Liquid rounding
      out.comparePrice = price;
      out.badgeType = 'pro';
      out.badgeLabel = s.stylistDiscountLabel || 'Pro Stylist Discount';
      out.badgePct = proPct;
    } else if (campaignPct > 0) {
      out.displayPrice = applyPct(price, campaignPct); // PL 2026-10-08 audit: Liquid rounding
      out.comparePrice = price;
      out.badgeType = 'campaign';
      out.badgeLabel = lcDrove
        ? (s.lastChancePromoLabel || 'Last Chance')
        : ((slot && slot.label) || s.promoVariantsSale || '');
      out.badgePct = campaignPct;
      out.lcActive = lcDrove;

      // Pro bonus on Fall Sale lines. Mirrors the SupaEasy function
      // (Professional Discounts v2.3) and the resolver: a further percentage
      // off the ALREADY REDUCED price, so 50% + 10% is 55% off retail, not 60%.
      // Applied to displayPrice rather than to price so client and server can
      // never drift apart. badgePct is recomputed from the final figure for the
      // same reason - adding the two would overstate.
      var proBonus = s.isProStylist ? (parseFloat(s.proBonusPercent || '0') || 0) : 0;
      if (lcDrove && proBonus > 0 && proPct > 0) {
        out.displayPrice = applyPct(out.displayPrice, proBonus); // PL 2026-10-08 audit: Liquid rounding
        out.badgeLabel = s.proBonusBadgeLabel || 'Pro Stylist Price';
        out.badgePct = Math.round((price - out.displayPrice) * 100 / price);
      }
    } else if (codePct > 0 && !excluded) {
      out.badgeType = 'code';
      out.badgeLabel = (slot && slot.label) || s.promoVariantsSale || '';
      out.badgePct = codePct;
    } else if (compare > 0 && !excluded) {
      // Baked last-chance renders like a markdown but carries the campaign name.
      out.badgeType = lcBaked ? 'campaign' : 'sale';
      out.badgeLabel = lcBaked ? (s.lastChancePromoLabel || 'Last Chance')
                               : (s.promoVariantsSale || '');
      out.badgePct = Math.round((compare - price) * 100 / compare);
      out.lcActive = lcBaked;
    }
    return out;
  }

  /* Build resolver inputs from the server-rendered .price-list attributes. This is
   * how theme.js re-resolves on variant change without product JSON:
   *   has-discount                  -> tag campaign percent
   *   data-lc-variants              -> variant-level last-chance membership (CSV of ids)
   *   data-pro-eligible             -> Pro discount percent when this customer+product
   *                                    is eligible (absent = not eligible)
   *   data-excluded-from-all-badges -> exclusion flag
   */
  function resolveFromDom(priceList, variant) {
    if (!priceList || !priceList.getAttribute || !variant) return resolve(null, variant);
    var s = settings();
    // data-lc-variants is a CSV of "id" or "id:tier" entries. One parse gives
    // both the membership test and the per-variant depth.
    var lcMap = {};
    String(priceList.getAttribute('data-lc-variants') || '').split(',').forEach(function (entry) {
      if (!entry) return;
      var parts = entry.split(':');
      if (!parts[0]) return;
      lcMap[parts[0]] = parts.length > 1 ? (parseInt(parts[1], 10) || 0) : 0;
    });
    var vid = String(variant.id);
    var isLc = Object.prototype.hasOwnProperty.call(lcMap, vid);
    var v = {
      price: variant.price,
      compare_at_price: variant.compare_at_price,
      last_chance: isLc,
      promo_tier: isLc && lcMap[vid] > 0 ? lcMap[vid] : null
    };
    var proAttr = priceList.getAttribute('data-pro-eligible');
    var productData = {
      tag_discount: parseInt(priceList.getAttribute('has-discount') || '0', 10) || 0,
      tags: [],
      excluded: priceList.getAttribute('data-excluded-from-all-badges') === 'true',
      pro_eligible: proAttr !== null && (parseInt(proAttr, 10) || 0) > 0
    };
    var opts = Object.create(s);
    if (proAttr !== null) {
      // The server only renders data-pro-eligible for a logged-in Pro on an eligible
      // product - trust it over the global flag so the attribute is self-sufficient.
      opts.stylistDiscount = proAttr;
      opts.isProStylist = (parseInt(proAttr, 10) || 0) > 0;
    }
    return resolve(productData, v, opts);
  }

  window.plPromo = { resolve: resolve, resolveFromDom: resolveFromDom, applyPct: applyPct, _tagPercent: tagPercent, _slotMap: slotMap };
})();